<template>
  <div v-if="time" class="timer-container">
    <svg
      class="timer-svg"
      viewBox="0 0 100 100"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g class="timer-group">
        <circle class="timer-circle" cx="50" cy="50" r="45" />
        <path
          :stroke-dasharray="dashStroke"
          class="timer-circle-remaining"
          :class="remainingPathColor"
          d="
          M 50, 50
          m -45, 0
          a 45,45 0 1,0 90,0
          a 45,45 0 1,0 -90,0
        "
        ></path>
      </g>
    </svg>
    <span class="timer-label">
      {{ formatTime() }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";

const props = defineProps<{
  time?: number;
}>();

let TOTAL_TIME = 0;
if (props.time) {
  TOTAL_TIME = +props.time;
}

const WARNING_THRESHOLD = 10;
const ALERT_THRESHOLD = 5;

const COLOR_CODES = {
  info: {
    color: "green",
  },
  warning: {
    color: "orange",
    threshold: WARNING_THRESHOLD,
  },
  alert: {
    color: "red",
    threshold: ALERT_THRESHOLD,
  },
  neutral: {
    color: "grey",
  },
};

const remainingPathColor = ref(COLOR_CODES.info.color);
const dashStroke = ref("283 283");

const timeLeft = ref(0);
timeLeft.value = TOTAL_TIME;

function formatTime() {
  const time = timeLeft.value;
  const minutes = Math.floor(timeLeft.value / 60);

  const seconds = time % 60;

  if (seconds < 10) {
    return `${minutes}:0${seconds}`;
  }

  return `${minutes}:${seconds}`;
}

function startTimer() {
  let timePassed = 0;
  const interval: number = setInterval(() => {
    timePassed += 1;
    timeLeft.value = TOTAL_TIME - timePassed;
    setCircleDasharray(timeLeft.value);
    setRemainingPathColor(timeLeft.value);
    if (timeLeft.value === 0) {
      clearInterval(interval);
    }
  }, 1000);
}

onMounted(() => {
  startTimer();
});

function calculateTimeFraction(timeLeft: number) {
  const rawTimeFraction = timeLeft / TOTAL_TIME;
  return rawTimeFraction - (1 / TOTAL_TIME) * (1 - rawTimeFraction);
}

// Update the dasharray value as time passes, starting with 283
function setCircleDasharray(timeLeft: number) {
  const gapDashStroke = (calculateTimeFraction(timeLeft) * 283).toFixed(0);
  if (+gapDashStroke <= 0) {
    dashStroke.value = `0 283`;
  } else {
    dashStroke.value = `${gapDashStroke} 283`;
  }
}

function setRemainingPathColor(timeLeft: number) {
  const { alert, warning, neutral } = COLOR_CODES;

  if (timeLeft <= 0) {
    remainingPathColor.value = neutral.color;
  } else if (timeLeft <= alert.threshold) {
    remainingPathColor.value = alert.color;
  } else if (timeLeft <= warning.threshold) {
    remainingPathColor.value = warning.color;
  }
}
</script>

<style scoped>
.timer-container {
  position: relative;
  width: 90%;
  max-width: 150px;
  padding-bottom: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.timer-group {
  fill: none;
  stroke: none;
}

.timer-circle {
  stroke-width: 7px;
  stroke: grey;
}

.timer-label {
  position: absolute;
  max-width: 150px !important;

  font-size: 25px;
  font-weight: 500;
}

.timer-circle-remaining {
  stroke-width: 7px;
  stroke-linecap: round;

  transform: rotate(90deg);
  transform-origin: center;

  transition: 1s linear all;

  stroke: currentColor;
}

.timer-svg {
  transform: scaleX(-1);
}

.timer-circle-remaining.green {
  color: rgb(65, 184, 131);
}

.timer-circle-remaining.orange {
  color: orange;
}

.timer-circle-remaining.red {
  color: red;
}

.timer-circle-remaining.grey {
  color: grey;
}
</style>
